import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import KaagazyLogoWhite from "../../assets/logo-white.png";
import { BillingAPI, PlanInfo } from "../../services/billing.service";
import { formatPlanTotal, formatPlanTaxBreakdown } from "../../utils/billing";
import { OnboardingAPI } from "../../services/onboarding.service";
import { refreshOnboardingCache } from "../../components/RequireAuth";
import { PLAN_BASE_ANNUAL_PRICE_INR } from "../../constants";

/* ------------------------------------------------------------------ */
/*  Plan data                                                          */
/* ------------------------------------------------------------------ */

const BASE_INCLUDED = [
  "Patient records & appointments",
  "Billing & invoices",
  "Basic reports",
  "Email & SMS notifications",
  "Offline sync",
];

const BASE_LOCKED = [
  "AI Scribe",
  "ABDM / ABHA sync",
  "Cloud backup (S3)",
  "Advanced analytics",
  "WhatsApp notifications",
];

/* ------------------------------------------------------------------ */
/*  Left-panel illustration                                            */
/* ------------------------------------------------------------------ */

const PlanArt: React.FC = () => (
  <svg viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[260px]">
    <ellipse cx="100" cy="165" rx="60" ry="7" fill="rgba(0,0,0,0.12)" />
    {/* Base card */}
    <rect x="16" y="40" width="88" height="112" rx="10" fill="white" fillOpacity="0.14" stroke="white" strokeOpacity="0.3" strokeWidth="1.2" />
    <rect x="24" y="52" width="40" height="6" rx="3" fill="white" fillOpacity="0.45" />
    <rect x="24" y="64" width="28" height="4" rx="2" fill="white" fillOpacity="0.25" />
    <rect x="24" y="76" width="64" height="1" fill="white" fillOpacity="0.15" />
    {[0,1,2,3,4].map((i) => (
      <g key={i}>
        <circle cx="30" cy={88 + i * 11} r="3" fill="white" fillOpacity="0.3" />
        <rect x="37" y={85 + i * 11} width="44" height="4" rx="2" fill="white" fillOpacity="0.2" />
      </g>
    ))}
    <rect x="24" y="136" width="72" height="10" rx="5" fill="white" fillOpacity="0.18" />
    {/* Premium card — elevated */}
    <rect x="96" y="26" width="92" height="120" rx="10" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.45" strokeWidth="1.5" />
    <rect x="104" y="36" width="76" height="20" rx="4" fill="white" fillOpacity="0.18" />
    <rect x="104" y="38" width="44" height="8" rx="3" fill="white" fillOpacity="0.5" />
    <rect x="152" y="38" width="22" height="8" rx="4" fill="white" fillOpacity="0.35" />
    <rect x="104" y="62" width="28" height="4" rx="2" fill="white" fillOpacity="0.25" />
    <rect x="104" y="74" width="68" height="1" fill="white" fillOpacity="0.15" />
    {[0,1,2,3,4,5].map((i) => (
      <g key={i}>
        <circle cx="110" cy={84 + i * 10} r="3" fill="white" fillOpacity="0.45" />
        <rect x="117" y={81 + i * 10} width="48" height="4" rx="2" fill="white" fillOpacity="0.25" />
      </g>
    ))}
    <rect x="104" y="126" width="76" height="12" rx="6" fill="white" fillOpacity="0.35" />
    {/* Star accent */}
    <circle cx="168" cy="20" r="10" fill="white" fillOpacity="0.2" />
    <text x="168" y="25" textAnchor="middle" fill="white" fillOpacity="0.7" fontSize="12">★</text>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Feature row sub-components                                         */
/* ------------------------------------------------------------------ */

const IncludedRow: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div className="flex items-start gap-2">
    <span className={`mt-0.5 text-[11px] font-bold flex-shrink-0 ${color}`}>✓</span>
    <span className="text-[11px] text-[#314158]">{text}</span>
  </div>
);

const LockedRow: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5 text-[10px] flex-shrink-0 text-[#9ca3af]">🔒</span>
    <span className="text-[11px] text-[#9ca3af]">{text}</span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main page component                                                */
/* ------------------------------------------------------------------ */

const SelectPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    BillingAPI.getPlans()
      .then((data) => setPlans(data ?? []))
      .catch(() => { /* plans unavailable — use static display */ });
  }, []);

  const annualPlans = plans.filter((p) => !p.is_trial && p.interval === "annual");

  const completeOnboarding = async () => {
    try {
      await OnboardingAPI.verifyEmail();
    } catch (err: any) {
      // 409 = already completed (skipDocuments path) — safe to proceed
      if (err?.statusCode !== 409) throw err;
    }
    await refreshOnboardingCache();
    navigate("/dashboard", { replace: true });
  };

  const handleComplete = async () => {
    setError(null);
    setLoading(true);
    try {
      await completeOnboarding();
    } catch {
      setError("Could not complete setup. Please try again.");
      setLoading(false);
    }
  };

  const basePlan = annualPlans.find((p) => p.tier === "base");
  const basePriceLabel = basePlan
    ? formatPlanTotal(basePlan)
    : `₹${PLAN_BASE_ANNUAL_PRICE_INR.toLocaleString("en-IN")}`;

  return (
    <div className="relative h-screen overflow-hidden flex">
      {/* Bottom gradient bleed */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{ background: "linear-gradient(to top, #7c3aed 0%, transparent 100%)", opacity: 0.1 }}
      />

      {/* ───── Left panel ───── */}
      <div
        className="hidden md:flex w-[40%] h-full flex-col relative"
        style={{ background: "linear-gradient(180deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)" }}
      >
        <div className="absolute top-0 left-6">
          <img
            src={KaagazyLogoWhite}
            alt="Kaagazy"
            className="h-72 object-contain"
            style={{ mixBlendMode: "screen", marginTop: "-60px" }}
          />
        </div>

        <div className="flex flex-col justify-start pt-60 px-10 lg:px-14 pb-10 text-white">
          <p className="text-base font-normal text-white/60 mb-3">Step 3 of 3</p>

          <h1 className="text-[44px] font-bold leading-[1.1] tracking-tight">
            Choose
            <br />
            Your Plan
          </h1>

          <p className="text-[17px] text-white/70 mt-6 leading-relaxed max-w-[360px]">
            Start with essentials or unlock AI Scribe, ABDM sync and advanced
            analytics with Base — billed annually.
          </p>

          <div className="mt-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-5 max-w-[360px] space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm">🔒</div>
              <p className="text-sm text-white/80">All plans include end-to-end encryption</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm">☁</div>
              <p className="text-sm text-white/80">Offline-first with cloud sync</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm">↑</div>
              <p className="text-sm text-white/80">Switch plans anytime, no lock-in</p>
            </div>
          </div>

          <div className="mt-10 flex justify-center opacity-80">
            <PlanArt />
          </div>
        </div>
      </div>

      {/* ───── Right panel ───── */}
      <div className="flex flex-col w-full md:w-[60%] bg-white h-full overflow-y-auto relative z-10 items-center">
        <div className="flex-1 px-4 sm:px-6 md:px-12 py-6 sm:py-10 md:py-12 max-w-[680px] w-full">
          {/* Mobile step indicator */}
          <div className="md:hidden mb-4">
            <p className="text-xs font-medium text-slate-400">Step 3 of 3</p>
          </div>

          {/* Header */}
          <div className="flex items-start gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#edfaff] border border-[#cefafe] flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[#00b8db] text-base leading-none">☁</span>
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-[#0f172b]">Choose your plan</h2>
              <p className="text-xs sm:text-sm text-[#62748e] mt-0.5">
                Unlock AI Scribe, ABDM sync &amp; advanced reports — billed annually
              </p>
            </div>
          </div>

          {/* Error alert */}
          {error && (
            <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-[11px] text-red-600">
              {error}
            </div>
          )}

          {/* Plan card */}
          <div className="mt-6 max-w-sm">
            <div className="relative rounded-[14px] border border-[#e5edf5] bg-[#fafcff] overflow-hidden flex flex-col">
              <div className="p-4 flex-1">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <p className="text-[16px] font-bold text-[#0f172b]">Base</p>
                    <p className="text-[11px] text-[#62748e] mt-0.5">Cloud-based, all essentials</p>
                  </div>
                </div>

                <p className="text-[26px] font-bold text-[#0f172b] mt-3">{basePriceLabel}</p>
                <p className="text-[11px] text-[#62748e]">/year</p>
                {/* The headline is the amount Razorpay actually charges. Spelling
                    out the tax keeps the plan page and Checkout telling the same
                    story, instead of an unexplained 18% jump at payment. */}
                {basePlan && formatPlanTaxBreakdown(basePlan) && (
                  <p className="text-[11px] text-[#62748e] mt-0.5">
                    {formatPlanTaxBreakdown(basePlan)}
                  </p>
                )}

                <div className="my-3 border-t border-[#e5edf5]" />

                <div className="space-y-[7px]">
                  {BASE_INCLUDED.map((f) => <IncludedRow key={f} text={f} color="text-[#00a33e]" />)}
                  {BASE_LOCKED.map((f) => <LockedRow key={f} text={f} />)}
                </div>
              </div>

              <div className="px-4 pb-4">
                <button
                  type="button"
                  onClick={handleComplete}
                  disabled={loading}
                  className="w-full rounded-[8px] border border-[#d1d5db] bg-white py-2.5 text-[13px] font-medium text-[#374151] hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Setting up…" : "Continue →"}
                </button>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <p className="mt-4 text-center text-[12px] text-[#00b8db]">
            Billed annually · exclusive of GST &nbsp;→&nbsp; Settings › Finance &amp; Billing
          </p>

          {/* Navigation row */}
          <div className="mt-8 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/onboarding/documents")}
              className="text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleComplete}
              disabled={loading}
              className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectPlanPage;
